/**
 * useDocumentDownload — fetch a short-lived link to a document and save it (`INT-9`).
 *
 * Why this exists: a customer asked to confirm what a document IS could previously
 * see only its filename, its folder, a guessed kind and two lines of extracted
 * text. After `INT-8` a technical drawing has no extracted text at all — we stop
 * reading it on purpose, because doing so cost up to 413 MB per file. So "ask a
 * human" needed to become an answerable question, and this is the escape hatch.
 *
 * Deliberately NOT a viewer. Omar, 28 Aug: *"A simple download link is enough…
 * the behaviour where the browser simply downloads the file into the customer's
 * Downloads folder is absolutely fine."*
 *
 * The busy latch is a `ref` mirrored into state, copied from
 * `useDocumentCorrection` for the reason recorded there: React state settles a
 * commit too late to stop a second click, and two clicks here would mint two
 * presigned URLs and start two saves of the same file.
 *
 * No react-query: see `fetchDocumentDownloadUrl`. Every `UploadView` harness
 * renders without a `QueryClientProvider`, and a single-use expiring URL has
 * nothing to cache.
 */
import { useRef, useState } from "react";

import { apiErrorMessage, useApi, type Api } from "./api";
import {
  fetchDocumentDownloadUrl,
  fetchPriceListFileDownloadUrl,
  type TenderDocument,
} from "./tenderApi";

export interface DocumentDownload {
  /** True while THIS document's link is being minted. */
  isBusy: (docId: string) => boolean;
  /** The error for THIS document, or null. Per-document so one failure is not
   *  reported on every row of the list. */
  errorFor: (docId: string) => string | null;
  /** Mint a link for this document and hand it to the browser to save. */
  open: (doc: TenderDocument) => Promise<void>;
  /** The same, by raw id — for things that are not `TenderDocument`s (a
   *  price-list workbook). `open` is kept because every existing call site
   *  holds a document, and narrowing them to `.id` would lose the type check
   *  that they hold one at all. */
  openId: (id: string) => Promise<void>;
}

/** The shared machinery: mint a short-lived URL for `id`, hand it to the
 * browser, keep a per-id busy latch and a per-id error.
 *
 * ONE implementation, two callers (`P-55`). A second hand-written downloader
 * beside this one would be two behaviours that agree today — and the parts most
 * likely to drift are the two that were hard to get right: the `ref`-mirrored
 * latch that actually stops a double click (React state settles a commit too
 * late), and NOT setting `a.download`, because browsers ignore that attribute
 * on a cross-origin href and it would silently defeat the server's
 * `Content-Disposition`.
 */
function useSignedDownload(mint: (api: Api, id: string) => Promise<string>): DocumentDownload {
  const api = useApi();
  const inFlight = useRef<Set<string>>(new Set());
  const [busy, setBusy] = useState<ReadonlySet<string>>(new Set());
  const [errors, setErrors] = useState<Readonly<Record<string, string>>>({});

  async function openId(id: string): Promise<void> {
    if (inFlight.current.has(id)) return;
    inFlight.current.add(id);
    setBusy(new Set(inFlight.current));
    setErrors((prev) => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
    try {
      const url = await mint(api, id);
      // The filename and the content type ride on the presigned URL itself,
      // because they are the only place that can carry them: a document is
      // stored as `.../original.pdf`, so without the server's
      // `Content-Disposition` every document in a bundle saves under the same
      // name. `a.download` cannot substitute for it — browsers ignore the
      // attribute on a cross-origin href, which is exactly what this is.
      const a = document.createElement("a");
      a.href = url;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      setErrors((prev) => ({ ...prev, [id]: apiErrorMessage(e) }));
    } finally {
      inFlight.current.delete(id);
      setBusy(new Set(inFlight.current));
    }
  }

  return {
    isBusy: (id) => busy.has(id),
    errorFor: (id) => errors[id] ?? null,
    open: (doc: TenderDocument) => openId(doc.id),
    openId,
  };
}

export function useDocumentDownload(): DocumentDownload {
  return useSignedDownload(fetchDocumentDownloadUrl);
}

/** `P-55` — download the exact price-list workbook this project prices against.
 *
 * Omar, 28 Aug, overruling my recommendation to drop the dead external link:
 * *"the client should be able to download the exact Regional Price List file
 * that was used for the analysis and verify it independently."* The endpoint
 * presigns the same `r2_key` the analysis stages, so "the file used" is true by
 * construction rather than by our keeping two copies in step.
 *
 * `projectId` is closed over rather than passed per call: the route is
 * project-scoped, and a caller that could pass a different project than the one
 * whose rows it is rendering is a caller that will.
 */
export function usePriceListFileDownload(projectId: string | null): DocumentDownload {
  return useSignedDownload((api, fileId) =>
    projectId
      ? fetchPriceListFileDownloadUrl(api, projectId, fileId)
      : Promise.reject(new Error("nessun progetto selezionato")),
  );
}
